import { capitalize } from 'lodash';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { getPricingTypeLabels, useQuotePartActions } from 'quotes/hooks';
import { mapResetPricingQuoteParts } from 'quotes/mappers';
import { IQuotePart, IResetPricingForm, IResetPricingPayload } from 'quotes/types';

import QuotePartResetPricingFormView from './QuotePartResetPricingFormView';
import { quotePartResetPricingSchema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: (closeSelection?: boolean) => void;
  onSubmitSuccess?: () => void;
  resetPricingFormPayload?: IResetPricingPayload;
}

const QuotePartResetPricingForm = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  resetPricingFormPayload = null,
}: IProps) => {
  const { t } = useTranslation();

  const { showSuccessNotification } = useNotificationContext();
  const { selectedRows, updateRows } = useAgGridClientTableContext();

  const { handleCliJob } = useCliJobHandler();
  const { resetPricing } = useQuotePartActions();

  const onSubmit = (reset: UseFormReset<IResetPricingForm>) => async (data: IResetPricingForm) => {
    const payload = mapResetPricingQuoteParts(data, selectedRows, resetPricingFormPayload);
    const type = capitalize(getPricingTypeLabels(payload.pricingTypes));

    handleCliJob(resetPricing, payload, {
      cliJobInProgressMessage: t('messageResetPricingTypeInProgress', { type }),
      onCliJobFinished: (cliJob) => {
        const quoteParts = cliJob.result as IQuotePart[];

        updateRows({ update: quoteParts }, { refreshCells: true, redrawRows: true });

        showSuccessNotification(
          {
            message: t('messageResetPricingTypeSuccess', {
              count: selectedRows.length,
              type,
            }),
          },
          NotificationPosition.TOP
        );
      },
    });

    if (onSubmitSuccess) {
      onSubmitSuccess();
    }

    handleOnClose(reset, true);
  };

  const handleOnClose = (reset: UseFormReset<IResetPricingForm>, closeSelection = false) => {
    reset({ price: false, cost: false });
    onClose(closeSelection);
  };

  return (
    <FormProvider schema={quotePartResetPricingSchema()}>
      <QuotePartResetPricingFormView
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        quotePartsCount={selectedRows?.length || resetPricingFormPayload?.quoteParts?.length || 0}
      />
    </FormProvider>
  );
};

export default QuotePartResetPricingForm;
